'use client'

import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useToast } from '@/components/ui/use-toast'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorDisplay } from '@/components/ui/error-display'
import { parseTRPCError } from '@/lib/utils/trpc-error-utils'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { EmailTemplateItem, useEmailTemplatesList, useTestEmailTemplate, useUpdateEmailTemplate } from '@/lib/api/emailTemplates'
import { EmailTemplateEditor } from '../components/EmailTemplateEditor'

const EmailTemplatePageSkeleton = () => (
    <div className="bg-background min-h-screen">
        <div className="border-b px-6 py-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <div>
                        <Skeleton className="h-5 w-48" />
                        <Skeleton className="mt-1 h-3 w-32" />
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-16" />
                </div>
            </div>
        </div>
        <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-[1fr,420px]">
            <div className="space-y-4">
                <Skeleton className="h-48 w-full rounded-lg" />
                <Skeleton className="h-32 w-full rounded-lg" />
                <Skeleton className="h-64 w-full rounded-lg" />
            </div>
            <Skeleton className="hidden h-96 w-full rounded-lg lg:block" />
        </div>
    </div>
)

const EditEmailTemplatePageInner = () => {
    const { emailTemplateId: emailId } = useParams<{ emailTemplateId: string }>()
    const { id: storeId } = useStoreNavigate()

    if (!emailId) {
        throw new Error('Email template ID is required')
    }

    const { data: emailTemplateList, isLoading, error } = useEmailTemplatesList({ storeId })

    if (isLoading) {
        return <EmailTemplatePageSkeleton />
    }

    if (error) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <ErrorDisplay error={parseTRPCError(error)} />
            </div>
        )
    }

    const emailTemplate = emailTemplateList?.templates.find((template) => template.id === emailId)

    if (!emailTemplate) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-2">
                <h2 className="text-lg font-semibold">Email template not found</h2>
                <p className="text-sm text-muted-foreground">The template may have been deleted.</p>
            </div>
        )
    }

    return <EditEmailTemplatePageContent emailTemplate={emailTemplate} />
}

const EditEmailTemplatePageContent = ({ emailTemplate }: { emailTemplate: EmailTemplateItem }) => {
    const { t } = useTranslation(['emails', 'common'])
    const { t: tCommon } = useTranslation('common')
    const { storeRoutes, id: storeId } = useStoreNavigate()
    const { toast } = useToast()

    const { mutate: testEmailTemplate } = useTestEmailTemplate()
    const updateMutation = useUpdateEmailTemplate()

    const handleSave = React.useCallback(
        async (data: Partial<EmailTemplateItem>) => {
            try {
                await updateMutation.mutateAsync({
                    id: emailTemplate.id,
                    data: { ...data, storeId } as any,
                })
                // Stay on the editor after save — merchants iterate quickly
                // and popping back to the list each time is hostile UX.
            } catch (error: any) {
                toast({
                    title: tCommon('error'),
                    description: error.message || t('saveError'),
                    variant: 'destructive',
                })
            }
        },
        [emailTemplate.id, storeId, updateMutation, toast, t, tCommon],
    )

    const handleTest = React.useCallback(
        async (email: string) => {
            if (!emailTemplate?.id) {
                toast({
                    title: tCommon('error'),
                    description: t('saveFirst'),
                    variant: 'destructive',
                })
                return
            }
            testEmailTemplate({
                templateId: emailTemplate.id,
                to: email,
                locale: 'en',
            })
        },
        [emailTemplate?.id, testEmailTemplate, toast, t, tCommon],
    )

    return (
        <EmailTemplateEditor
            template={emailTemplate}
            onSave={handleSave}
            onTest={handleTest}
            isLoading={updateMutation.isPending}
            title={t('editTemplate')}
            subtitle={emailTemplate.name}
            backTo={storeRoutes.EMAILS()}
        />
    )
}

export const EditEmailTemplatePage = withRequiredPermission(EditEmailTemplatePageInner, PERMISSIONS.STORES_MANAGE)
